import { Capacitor } from '@capacitor/core';

const STORAGE_KEY = 'odl.lastRoute';

// Paths we never want to persist or restore. Login/auth flow,
// callback URLs, and similar one-shot routes should not be the
// destination on a cold boot.
const SKIP_PATH_PREFIXES = ['/login', '/callback', '/logout', '/forgot-password', '/password-reset'];

// Paths that count as "home" — if cold boot lands here, we'll
// consider restoring a deeper saved path. Routes outside this set
// are respected (the user is already where they wanted to be).
const HOME_PATHS = new Set(['/', '/support/customers']);

export const isMobile = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const isHomePath = pathname => {
  if (!pathname) return true;
  if (HOME_PATHS.has(pathname)) return true;
  // /c/<id>/plants is the regular-customer home that ``sendHome``
  // navigates to. Treat it as a home path too — if we land there
  // on cold boot we should consider restoring a deeper saved path.
  return /^\/c\/[^/]+\/plants\/?$/.test(pathname);
};

const shouldPersist = pathname => {
  if (!pathname) return false;
  if (pathname === '/') return false;
  return !SKIP_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));
};

// Query-string keys that we never want to persist — primarily auth /
// credential carriers. Defense in depth: the app today doesn't put
// any of these in the URL, but if a future flow does (oauth callback,
// magic link, etc.) we don't want them to land in localStorage.
const SENSITIVE_QUERY_KEYS = new Set([
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'code',
  'auth',
  'authorization',
  'password',
  'secret',
  'api_key',
  'apikey',
  'session',
  'state',
]);

const sanitizeSearch = search => {
  if (!search) return '';
  // URLSearchParams accepts a leading '?'.
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  let mutated = false;
  for (const key of Array.from(params.keys())) {
    if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
      params.delete(key);
      mutated = true;
    }
  }
  if (!mutated) return search;
  const stripped = params.toString();
  return stripped ? `?${stripped}` : '';
};

export const persistRoute = (pathname, search = '') => {
  if (!isMobile()) return;
  if (!shouldPersist(pathname)) return;
  try {
    const safeSearch = sanitizeSearch(search);
    window.localStorage.setItem(STORAGE_KEY, `${pathname}${safeSearch}`);
  } catch {
    // localStorage can throw when disk is full or storage is
    // disabled. We'd rather skip persistence than crash the app.
  }
};

export const readPersistedRoute = () => {
  if (!isMobile()) return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};
